import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Drawer,
  Divider,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
  Stack,
  Badge,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  fetchCorrelationSummary,
  fetchCorrelationIncidents,
  fetchIncidentCorrelationDetail,
  fetchIncidentLiveAlerts,
  fetchFilterOptions,
  LiveAlertsResult,
  CorrelationSummary,
  CorrelationIncident,
  IncidentDetail,
  CorrelationConfidence,
  GapType,
  AlertSignal,
  FilterOptions,
} from '../services/preIncidentCorrelationService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return iso;
  }
};

const toInputDate = (iso: string): string => iso.slice(0, 10);

const CONFIDENCE_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  High:   { label: 'High',   color: '#b71c1c', bg: '#ffebee' },
  Medium: { label: 'Medium', color: '#e65100', bg: '#fff3e0' },
  Low:    { label: 'Low',    color: '#827717', bg: '#f9fbe7' },
  none:   { label: 'None',   color: '#757575', bg: '#f5f5f5' },
};

const GAP_STYLE: Record<GapType | string, { label: string; color: string; bg: string }> = {
  monitoring_triggered:      { label: 'Captured by Monitoring',       color: '#2e7d32', bg: '#e8f5e9' },
  alerts_not_acted:          { label: 'Alerts Not Acted On',          color: '#b71c1c', bg: '#ffebee' },
  monitoring_no_escalation:  { label: 'Monitoring — No Escalation',   color: '#e65100', bg: '#fff3e0' },
  monitoring_gap:            { label: 'Monitoring Gap',               color: '#4a148c', bg: '#f3e5f5' },
  no_ci_mapping:             { label: 'CI Not Mapped',                color: '#757575', bg: '#f5f5f5' },
};

const PRIORITY_STYLE: Record<string, { color: string; bg: string }> = {
  '1': { color: '#b71c1c', bg: '#ffebee' },
  '2': { color: '#bf360c', bg: '#fbe9e7' },
};

const ConfidenceChip: React.FC<{ value: CorrelationConfidence }> = ({ value }) => {
  const s = value ? CONFIDENCE_STYLE[value] : CONFIDENCE_STYLE.none;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ fontWeight: 600, fontSize: '0.7rem', color: s.color, bgcolor: s.bg, border: `1px solid ${s.color}33` }}
    />
  );
};

const GapChip: React.FC<{ value: string }> = ({ value }) => {
  const s = GAP_STYLE[value] ?? { label: value, color: '#757575', bg: '#f5f5f5' };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ fontWeight: 500, fontSize: '0.7rem', color: s.color, bgcolor: s.bg }}
    />
  );
};

const PriorityChip: React.FC<{ value: string }> = ({ value }) => {
  const s = PRIORITY_STYLE[value] ?? { color: '#0067b3', bg: '#e3f2fd' };
  const label = value === '1' ? 'P1' : value === '2' ? 'P2' : value;
  return (
    <Chip
      label={label}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.72rem', color: s.color, bgcolor: s.bg, minWidth: 36 }}
    />
  );
};

// ---------------------------------------------------------------------------
// Summary stat card
// ---------------------------------------------------------------------------
interface StatCardProps {
  label: string;
  value: number | string;
  sub?: string;
  color?: string;
  icon?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, color = '#0067b3', icon }) => (
  <Paper
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2.5,
      borderTop: `3px solid ${color}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
        {label}
      </Typography>
      {icon && <Box sx={{ color }}>{icon}</Box>}
    </Box>
    <Typography variant="h4" sx={{ fontWeight: 700, color, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: '#888' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ---------------------------------------------------------------------------
// Detail drawer – tabs
// ---------------------------------------------------------------------------
interface DetailDrawerProps {
  detail: IncidentDetail | null;
  loading: boolean;
  onClose: () => void;
  initialTab?: number;
  autoFetchLive?: boolean;
}

const DetailDrawer: React.FC<DetailDrawerProps> = ({ detail, loading, onClose, initialTab = 0, autoFetchLive = false }) => {
  const [tab, setTab] = useState(initialTab);
  const [liveAlerts, setLiveAlerts]     = useState<LiveAlertsResult | null>(null);
  const [liveLoading, setLiveLoading]   = useState(false);
  const [liveError, setLiveError]       = useState<string | null>(null);

  const incidentAgeDays = detail
    ? Math.floor((Date.now() - new Date(detail.incident.sys_created_on_value).getTime()) / 86_400_000)
    : 0;
  const isOldIncident = incidentAgeDays > 30;

  useEffect(() => {
    setTab(initialTab);
    setLiveAlerts(null);
    setLiveError(null);
  }, [detail, initialTab]);

  const handleFetchLiveAlerts = async () => {
    if (!detail) return;
    setLiveLoading(true);
    setLiveError(null);
    try {
      const result = await fetchIncidentLiveAlerts(detail.incident.number);
      setLiveAlerts(result);
    } catch (e: any) {
      setLiveError(e.message || 'Failed to fetch live alerts');
    } finally {
      setLiveLoading(false);
    }
  };

  // Auto-trigger live fetch when drawer opens with autoFetchLive=true
  useEffect(() => {
    if (autoFetchLive && detail && !liveAlerts && !liveLoading) {
      handleFetchLiveAlerts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetchLive, detail]);

  return (
    <Drawer
      anchor="right"
      open={Boolean(detail) || loading}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: '52%' }, p: 0, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e6eb', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {detail && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PriorityChip value={detail.incident.priority} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#001a35', fontSize: '1rem' }}>
                  {detail.incident.number}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.4 }}>
                {detail.incident.short_description}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
                Opened: {formatDate(detail.incident.sys_created_on_value)}
                {detail.incident.resolved_at && ` · Resolved: ${formatDate(detail.incident.resolved_at)}`}
              </Typography>
            </>
          )}
          {loading && <Typography variant="body2" color="textSecondary">Loading correlation details…</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {detail && !loading && (
        <>
          {/* Correlation summary band */}
          <Box sx={{ px: 3, py: 1.5, bgcolor: '#f8f9fb', borderBottom: '1px solid #e0e6eb', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Best Confidence</Typography>
              <Box mt={0.3}><ConfidenceChip value={detail.correlation_summary.best_confidence} /></Box>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gap Type</Typography>
              <Box mt={0.3}><GapChip value={detail.correlation_summary.gap_type} /></Box>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Precursors</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: detail.correlation_summary.precursor_count > 0 ? '#b71c1c' : '#444' }}>
                {detail.correlation_summary.precursor_count} P3/P4 incident{detail.correlation_summary.precursor_count !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Alerts</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: detail.correlation_summary.alert_signal_count > 0 ? '#e65100' : '#444' }}>
                {detail.correlation_summary.alert_signal_count}
                <Typography component="span" variant="caption" sx={{ color: '#888', ml: 0.5 }}>
                  ({detail.correlation_summary.pre_incident_alert_count} pre / {detail.correlation_summary.during_incident_alert_count} during)
                </Typography>
              </Typography>
            </Box>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e0e6eb', minHeight: 40, px: 2 }}>
            <Tab label="Precursors" sx={{ fontSize: '0.82rem', minHeight: 40, textTransform: 'none', fontWeight: 600 }} />
            <Tab label={
              <Badge badgeContent={detail.correlation_summary.alert_signal_count} color="warning" max={99}>
                Alert Signals
              </Badge>
            } sx={{ fontSize: '0.82rem', minHeight: 40, textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Resolution Notes" sx={{ fontSize: '0.82rem', minHeight: 40, textTransform: 'none', fontWeight: 600 }} />
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Tab 0 – Precursor incidents */}
            {tab === 0 && (
              detail.precursors.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No P3/P4 precursor incidents found in the 12 hours before this incident.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  {detail.precursors.map((pre) => (
                    <Paper key={pre.number} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${CONFIDENCE_STYLE[pre.correlation_confidence]?.color ?? '#ccc'}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PriorityChip value={pre.priority} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#001a35' }}>{pre.number}</Typography>
                        <ConfidenceChip value={pre.correlation_confidence} />
                        {pre.minutes_before_p1p2 !== null && (
                          <Chip
                            label={`${pre.minutes_before_p1p2}m before P1/P2`}
                            size="small"
                            sx={{ fontSize: '0.68rem', bgcolor: '#f5f5f5', color: '#555' }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#333', mb: 0.8 }}>
                        {pre.short_description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {pre.correlation_reasons.map((r) => (
                          <Chip key={r} label={r.replace(/_/g, ' ')} size="small"
                            sx={{ fontSize: '0.67rem', bgcolor: '#e3f2fd', color: '#0067b3' }} />
                        ))}
                        {pre.matched_keywords && pre.matched_keywords.map((kw) => (
                          <Chip key={kw} label={kw} size="small"
                            sx={{ fontSize: '0.67rem', bgcolor: '#fff9c4', color: '#827717' }} />
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
                        {formatDate(pre.sys_created_on_value)}
                        {pre.cmdb_ci_display_value && ` · CI: ${pre.cmdb_ci_display_value}`}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )
            )}

            {/* Tab 1 – Alert signals */}
            {tab === 1 && (() => {
              const preSignals    = detail.alert_signals.filter(s => s.window_type === 'pre_incident');
              const duringSignals = detail.alert_signals.filter(s => s.window_type === 'during_incident');
              const preTotal      = preSignals.reduce((sum, s) => sum + (s.alert_count ?? 0), 0);
              const duringTotal   = duringSignals.reduce((sum, s) => sum + (s.alert_count ?? 0), 0);

              const AlertTable = ({ rows, emptyMsg }: { rows: AlertSignal[]; emptyMsg: string }) => {
                // Build SNR deep-link: 12h before incident open → resolved
                const buildSnrUrl = (scope: string) => {
                  const opened = new Date(detail.incident.sys_created_on_value);
                  const start  = new Date(opened.getTime() - 12 * 3_600_000);
                  const end    = detail.incident.resolved_at
                    ? new Date(detail.incident.resolved_at)
                    : new Date();
                  const fmt = (d: Date) => d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
                  return `https://ops-excellence.maersk.io/signal-to-noise?hedwigScope=${encodeURIComponent(scope)}&startTime=${fmt(start)}&endTime=${fmt(end)}`;
                };

                return rows.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', py: 1 }}>{emptyMsg}</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Time</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Alert Count</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>SNR Value</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Quick-Resolved</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Scope</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((sig, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(sig.calculated_at)}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: (sig.alert_count ?? 0) > 20 ? '#b71c1c' : '#333' }}>
                              {sig.alert_count ?? '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.78rem' }}>
                              {sig.snr_value !== null ? `${sig.snr_value}%` : '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.78rem' }}>{sig.quick_resolved_alerts ?? '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.78rem' }}>
                              {sig.scope_name ? (
                                <Tooltip title="Open in Signal-to-Noise Ratio page (12h window)" arrow>
                                  <a href={buildSnrUrl(sig.scope_name)} target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#0067b3', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                    {sig.scope_name} <OpenInNewIcon sx={{ fontSize: 12 }} />
                                  </a>
                                </Tooltip>
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              };

              const dbPanel = detail.alert_signals.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No SNR alert records found for this application across the pre-incident or incident window.
                </Alert>
              ) : (
                <Stack spacing={2.5}>
                  {/* Pre-incident section */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label="Pre-Incident (12h before)" size="small"
                        sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: '0.72rem' }} />
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {preSignals.length} snapshot{preSignals.length !== 1 ? 's' : ''} · 
                        <strong style={{ color: preTotal > 0 ? '#e65100' : 'inherit' }}>{preTotal} total alerts</strong>
                      </Typography>
                    </Box>
                    <AlertTable rows={preSignals} emptyMsg="No alert snapshots in the 12 hours before this incident." />
                  </Box>

                  <Divider />

                  {/* During-incident section – collapsed when empty */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: duringSignals.length > 0 ? 1 : 0 }}>
                      <Chip label="During Incident" size="small"
                        sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontWeight: 700, fontSize: '0.72rem' }} />
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {duringSignals.length} snapshot{duringSignals.length !== 1 ? 's' : ''} ·{' '}
                        <strong style={{ color: duringTotal > 0 ? '#b71c1c' : 'inherit' }}>{duringTotal} total alerts</strong>
                        {duringSignals.length === 0 && (
                          <span style={{ color: '#aaa', marginLeft: 4 }}>— no snapshots while open</span>
                        )}
                      </Typography>
                    </Box>
                    {duringSignals.length > 0 && (
                      <AlertTable rows={duringSignals} emptyMsg="No alert snapshots recorded while the incident was open." />
                    )}
                  </Box>
                </Stack>
              );

              // ── Live Alerts panel (shown after user clicks the button) ──
              const livePanel = liveAlerts ? (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f7ff', border: '1px solid #90caf9', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0067b3' }}>
                      Live Hedwig Results
                    </Typography>
                    <Chip label={`${liveAlerts.data.window_start} → ${liveAlerts.data.window_end}`}
                      size="small" sx={{ fontSize: '0.68rem', bgcolor: '#e3f2fd', color: '#0067b3' }} />
                  </Box>
                  {/* Retention warning for old incidents */}
                  {isOldIncident && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1.5,
                      bgcolor: '#fff3e0', px: 1.5, py: 0.75, borderRadius: 0.5,
                      border: '1px solid #ffcc02', color: '#5d4037' }}>
                      ⚠️ This incident is <strong>{incidentAgeDays} days old</strong>. Hedwig retains alert
                      history for ~30–60 days, so live results may be incomplete.
                      The DB snapshots below are the reliable source for this incident.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
                    {[
                      { label: 'Total Alerts', val: liveAlerts.data.total_alert_count, color: '#e65100' },
                      { label: 'Pre-Incident (12h)', val: liveAlerts.data.pre_incident_alert_count, color: '#f57c00' },
                      { label: 'During Incident', val: liveAlerts.data.during_incident_alert_count, color: '#b71c1c' },
                      { label: 'Quick-Resolved (noise)', val: liveAlerts.data.quick_resolved_count, color: '#555' },
                      { label: 'SNR', val: liveAlerts.data.snr_value !== null ? `${liveAlerts.data.snr_value}%` : '—', color: '#2e7d32' },
                    ].map(({ label, val, color }) => (
                      <Box key={label}>
                        <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color }}>{val}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Non-prod filter note */}
                  {liveAlerts.data.non_prod_filtered_count > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#795548', mb: 1,
                      bgcolor: '#fff8e1', px: 1, py: 0.5, borderRadius: 0.5, border: '1px solid #ffe082' }}>
                      ⚠️ {liveAlerts.data.non_prod_filtered_count} non-prod alerts filtered out
                      (raw from API: {liveAlerts.data.raw_alert_count})
                    </Typography>
                  )}
                  {liveAlerts.data.alerts_by_scope.length > 0 && (() => {
                    // Build SNR deep-link from the live window timestamps
                    const buildLiveSnrUrl = (scope: string) => {
                      const fmt = (iso: string) => iso.slice(0, 16); // trim to YYYY-MM-DDTHH:mm
                      const start = fmt(liveAlerts.data.window_start);
                      const end   = fmt(liveAlerts.data.window_end);
                      return `https://ops-excellence.maersk.io/signal-to-noise?hedwigScope=${encodeURIComponent(scope)}&startTime=${encodeURIComponent(start)}&endTime=${encodeURIComponent(end)}`;
                    };
                    return (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Scope</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Pre-Incident</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>During</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Quick-Resolved</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {liveAlerts.data.alerts_by_scope.map((s) => (
                              <TableRow key={s.scope_name} hover>
                                <TableCell sx={{ fontSize: '0.78rem' }}>
                                  <Tooltip title="Open in Signal-to-Noise Ratio page" arrow>
                                    <a href={buildLiveSnrUrl(s.scope_name)} target="_blank" rel="noopener noreferrer"
                                      style={{ color: '#0067b3', display: 'inline-flex', alignItems: 'center', gap: 2, fontWeight: 600 }}>
                                      {s.scope_name} <OpenInNewIcon sx={{ fontSize: 12 }} />
                                    </a>
                                  </Tooltip>
                                  {s.non_prod_filtered > 0 && (
                                    <Typography component="span" variant="caption"
                                      sx={{ ml: 0.5, color: '#f57c00', fontStyle: 'italic' }}>
                                      ({s.non_prod_filtered} non-prod filtered)
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>{s.total_alerts}</TableCell>
                                <TableCell align="center" sx={{ color: '#f57c00' }}>{s.pre_incident_alerts}</TableCell>
                                <TableCell align="center" sx={{ color: '#b71c1c' }}>{s.during_incident_alerts}</TableCell>
                                <TableCell align="center" sx={{ color: '#888' }}>{s.quick_resolved_alerts}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Box>
              ) : null;

              return (
                <Stack spacing={0}>
                  {/* Fetch Live Alerts button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5,
                    p: 1.5, bgcolor: '#fffde7', border: '1px solid #ffe082', borderRadius: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#5d4037' }}>
                        Fetch Live Alerts from Hedwig
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#795548' }}>
                        Calls the Hedwig API directly for this incident’s exact time window.
                        Use this when DB snapshots show 0 alerts or for recent incidents.
                        {isOldIncident && (
                          <span style={{ color: '#bf360c', fontWeight: 600 }}>
                            {' '}⚠️ Incident is {incidentAgeDays} days old — live data may be partial.
                          </span>
                        )}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={liveLoading}
                      onClick={handleFetchLiveAlerts}
                      startIcon={liveLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                      sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#e65100' }, whiteSpace: 'nowrap', minWidth: 140 }}
                    >
                      {liveLoading ? 'Fetching…' : 'Fetch Live Alerts'}
                    </Button>
                  </Box>
                  {liveError && <Alert severity="error" sx={{ mb: 1.5 }}>{liveError}</Alert>}
                  {livePanel}

                  {/* Divider between live results and DB snapshots */}
                  {detail.alert_signals.length > 0 && (
                    <Box sx={{ mt: liveAlerts ? 2 : 0 }}>
                      <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1,
                        borderTop: liveAlerts ? '1px dashed #e0e6eb' : 'none', pt: liveAlerts ? 1.5 : 0 }}>
                        DB Snapshots (from signal_to_noise_ratio_history)
                      </Typography>
                    </Box>
                  )}

                  {/* Existing DB-backed signal tables */}
                  {dbPanel}
                </Stack>
              );
            })()}

            {/* Tab 2 – Resolution notes */}
            {tab === 2 && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#001a35', mb: 0.5 }}>Work Notes</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', maxHeight: 240, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', color: '#333' }}>
                      {detail.incident.work_notes || '(No work notes available)'}
                    </Typography>
                  </Paper>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#001a35', mb: 0.5 }}>Close Notes</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', maxHeight: 240, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', color: '#333' }}>
                      {detail.incident.close_notes || '(No close notes available)'}
                    </Typography>
                  </Paper>
                </Box>
                {detail.correlation_summary.keywords_used.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#001a35', mb: 0.5 }}>
                      Keywords Used for Correlation
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {detail.correlation_summary.keywords_used.map((kw) => (
                        <Chip key={kw} label={kw} size="small"
                          sx={{ fontSize: '0.7rem', bgcolor: '#fff9c4', color: '#827717' }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type SortField = 'sys_created_on_value' | 'precursor_count' | 'alert_signal_count' | 'priority';
type SortDir   = 'asc' | 'desc';

const DEFAULT_FROM = (() => {
  const d = new Date(); d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
})();
const DEFAULT_TO = new Date().toISOString().slice(0, 10);

const PreIncidentCorrelation: React.FC = () => {
  const [fromDate, setFromDate]           = useState(DEFAULT_FROM);
  const [toDate, setToDate]               = useState(DEFAULT_TO);
  const [summary, setSummary]             = useState<CorrelationSummary | null>(null);
  const [incidents, setIncidents]         = useState<CorrelationIncident[]>([]);
  const [totalCount, setTotalCount]       = useState(0);
  const [page, setPage]                   = useState(0);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [confFilter, setConfFilter]       = useState('');
  const [gapFilter, setGapFilter]         = useState('');
  const [portfolio, setPortfolio]         = useState('');
  const [platform, setPlatform]           = useState('');
  const [userReportedOnly, setUserReportedOnly] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ portfolios: [], platforms: [] });
  const [sortField, setSortField]         = useState<SortField>('sys_created_on_value');
  const [sortDir, setSortDir]             = useState<SortDir>('desc');

  // Detail drawer
  const [selectedNumber, setSelectedNumber]   = useState<string | null>(null);
  const [detail, setDetail]                   = useState<IncidentDetail | null>(null);
  const [detailLoading, setDetailLoading]     = useState(false);
  const [drawerInitialTab, setDrawerInitialTab] = useState(0);
  const [drawerAutoFetch, setDrawerAutoFetch]   = useState(false);

  const PAGE_SIZE = 50;

  // Load filter options once on mount
  useEffect(() => {
    fetchFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  const load = useCallback(async (resetPage = false) => {
    const currentPage = resetPage ? 0 : page;
    if (resetPage) setPage(0);
    setLoading(true);
    setError(null);
    try {
      const [sum, list] = await Promise.all([
        fetchCorrelationSummary(fromDate, toDate, { portfolio, platform, userReportedOnly }),
        fetchCorrelationIncidents({
          fromDate,
          toDate,
          limit: PAGE_SIZE,
          offset: currentPage * PAGE_SIZE,
          confidence: confFilter,
          gapType: gapFilter,
          portfolio,
          platform,
          userReportedOnly,
        }),
      ]);
      setSummary(sum);
      setIncidents(list.data);
      setTotalCount(list.total_count);
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, confFilter, gapFilter, portfolio, platform, userReportedOnly, page]);

  useEffect(() => { load(true); }, [fromDate, toDate, confFilter, gapFilter, portfolio, platform, userReportedOnly]);
  useEffect(() => { if (page > 0) load(); }, [page]);

  // Load detail when an incident is selected
  useEffect(() => {
    if (!selectedNumber) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    fetchIncidentCorrelationDetail(selectedNumber)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [selectedNumber]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...incidents].sort((a, b) => {
    let av: any = a[sortField] ?? '';
    let bv: any = b[sortField] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const quickRange = (days: number) => {
    const to = new Date().toISOString().slice(0, 10);
    const fr = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    setFromDate(fr); setToDate(to);
  };

  const CONF_OPTIONS  = ['', 'High', 'Medium', 'Low', 'None'] as const;
  const GAP_OPTIONS   = ['', 'monitoring_triggered', 'alerts_not_acted', 'monitoring_no_escalation', 'monitoring_gap', 'no_ci_mapping'] as const;
  const GAP_LABELS: Record<string, string> = {
    '': 'All Gap Types',
    monitoring_triggered:     'Captured by Monitoring',
    alerts_not_acted:         'Alerts Not Acted On',
    monitoring_no_escalation: 'Monitoring — No Escalation',
    monitoring_gap:           'Monitoring Gap',
    no_ci_mapping:            'CI Not Mapped',
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#001a35', mb: 0.5 }}>
          Pre-Incident Alert Correlation
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', maxWidth: 700 }}>
          Analyses <strong>all P1/P2 incidents</strong> and surfaces P3/P4 incidents, monitoring alerts, and
          warning signals that appeared in the <strong>12 hours before</strong> each major incident.
          Use the toggle to narrow to incidents <em>not captured through alerts</em> (excludes monitoring-triggered).
        </Typography>
      </Box>

      {/* Filters row */}
      <Paper sx={{ p: 2, mb: 2.5, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {/* Date range */}
        <TextField
          label="From"
          type="date"
          size="small"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[7, 30, 90].map(d => (
            <Button key={d} size="small" variant="outlined" onClick={() => quickRange(d)}
              sx={{ fontSize: '0.72rem', py: 0.3, minWidth: 50 }}>
              {d}d
            </Button>
          ))}
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Portfolio dropdown */}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Portfolio</InputLabel>
          <Select
            value={portfolio}
            label="Portfolio"
            onChange={e => setPortfolio(e.target.value)}
          >
            <MenuItem value=""><em>All Portfolios</em></MenuItem>
            {filterOptions.portfolios.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Platform dropdown */}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Platform</InputLabel>
          <Select
            value={platform}
            label="Platform"
            onChange={e => setPlatform(e.target.value)}
          >
            <MenuItem value=""><em>All Platforms</em></MenuItem>
            {filterOptions.platforms.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem />

        {/* Confidence filter */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#888', fontWeight: 600 }}>Confidence:</Typography>
          {CONF_OPTIONS.map(opt => (
            <Chip
              key={opt || 'all'}
              label={opt || 'All'}
              size="small"
              clickable
              onClick={() => setConfFilter(opt)}
              sx={{
                fontWeight: confFilter === opt ? 700 : 400,
                bgcolor: confFilter === opt ? '#0067b3' : '#f0f0f0',
                color:   confFilter === opt ? '#fff' : '#444',
                fontSize: '0.72rem',
              }}
            />
          ))}
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Gap type filter */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#888', fontWeight: 600 }}>Gap:</Typography>
          {GAP_OPTIONS.map(opt => (
            <Chip
              key={opt || 'all'}
              label={GAP_LABELS[opt]}
              size="small"
              clickable
              onClick={() => setGapFilter(opt)}
              sx={{
                fontWeight: gapFilter === opt ? 700 : 400,
                bgcolor: gapFilter === opt ? '#0067b3' : '#f0f0f0',
                color:   gapFilter === opt ? '#fff' : '#444',
                fontSize: '0.72rem',
              }}
            />
          ))}
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Not Captured Through Alerts toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={userReportedOnly}
              onChange={e => setUserReportedOnly(e.target.checked)}
              size="small"
              color="warning"
            />
          }
          label={
            <Typography variant="caption" sx={{ fontWeight: 600, color: userReportedOnly ? '#e65100' : '#888' }}>
              Not Captured Through Alerts
            </Typography>
          }
        />

        <Box sx={{ ml: 'auto' }}>
          <Button
            startIcon={<RefreshIcon />}
            size="small"
            variant="outlined"
            onClick={() => load(true)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Summary stat cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          <StatCard
            label="P1/P2 Incidents Analysed"
            value={summary.total_p1p2}
            sub={`${summary.total_p1} P1 · ${summary.total_p2} P2${userReportedOnly ? ' (not captured through alerts)' : ' (all incidents)'}`}
            color="#0067b3"
            icon={<InfoIcon fontSize="small" />}
          />
          <StatCard
            label="With Early Warning Signals"
            value={`${summary.precursor_rate}%`}
            sub={`${summary.with_any_signal} of ${summary.total_p1p2} had precursors`}
            color="#e65100"
            icon={<WarningIcon fontSize="small" />}
          />
          <StatCard
            label="High-Confidence Correlations"
            value={summary.high_confidence_count}
            sub="Same application & same CI"
            color="#b71c1c"
            icon={<WarningIcon fontSize="small" />}
          />
          <StatCard
            label="Monitoring Gaps"
            value={`${summary.monitoring_gap_rate}%`}
            sub={`${summary.no_precursors_count} incidents with no alerts at all`}
            color="#4a148c"
            icon={<CheckCircleIcon fontSize="small" />}
          />
        </Box>
      )}

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Incidents table */}
      <Paper>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e6eb', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#001a35' }}>
            P1/P2 Incidents
          </Typography>
          <Chip label={`${totalCount} total`} size="small" sx={{ fontSize: '0.72rem', bgcolor: '#e3f2fd', color: '#0067b3' }} />
          {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          <Box sx={{ ml: 'auto' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const headers = ['Number','Priority','Description','Application','Opened At','Resolved At',
                  'Precursors','Alerts (Total)','Pre-Incident Alerts','Confidence','Gap Type'];
                const rows = incidents.map(i => [
                  i.number, i.priority === '1' ? 'P1' : 'P2',
                  `"${(i.short_description || '').replace(/"/g, '""')}"`,
                  i.cmdb_ci_u_application_id || i.cmdb_ci_display_value || '',
                  i.sys_created_on_value, i.resolved_at || '',
                  i.precursor_count, i.alert_signal_count, i.pre_incident_alert_count,
                  i.best_correlation_confidence || 'None',
                  GAP_LABELS[i.gap_type] || i.gap_type,
                ]);
                const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                const a = document.createElement('a');
                a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                a.download = `pre-incident-correlation-${fromDate}-to-${toDate}.csv`;
                a.click();
              }}
              sx={{ fontSize: '0.72rem' }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f5f7fa', fontWeight: 700, fontSize: '0.78rem', color: '#001a35', whiteSpace: 'nowrap' } }}>
                <TableCell>
                  <TableSortLabel active={sortField === 'sys_created_on_value'} direction={sortDir} onClick={() => handleSort('sys_created_on_value')}>
                    Opened At
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'priority'} direction={sortDir} onClick={() => handleSort('priority')}>
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell>Incident</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Application / CI</TableCell>
                <TableCell align="center">
                  <TableSortLabel active={sortField === 'precursor_count'} direction={sortDir} onClick={() => handleSort('precursor_count')}>
                    Precursors
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel active={sortField === 'alert_signal_count'} direction={sortDir} onClick={() => handleSort('alert_signal_count')}>
                    Alerts (Total)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Pre-Incident (12h)</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Gap Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((inc) => (
                <TableRow
                  key={inc.number}
                  hover
                  selected={selectedNumber === inc.number}
                  onClick={() => setSelectedNumber(inc.number)}
                  sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: '#e8f0fe' } }}
                >
                  <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {formatDate(inc.sys_created_on_value)}
                  </TableCell>
                  <TableCell><PriorityChip value={inc.priority} /></TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#0067b3', whiteSpace: 'nowrap' }}>
                    {inc.number}
                    {inc.contact_type_display_value?.toLowerCase() === 'alert' && (
                      <Tooltip title="Monitoring-triggered incident" arrow>
                        <Chip label="⚡ Auto" size="small"
                          sx={{ ml: 0.5, fontSize: '0.62rem', height: 16, bgcolor: '#e8f5e9', color: '#2e7d32',
                            fontWeight: 700, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', maxWidth: 280 }}>
                    <Tooltip title={inc.short_description || ''}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inc.short_description || '—'}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#555', maxWidth: 200 }}>
                    <Tooltip title={`${inc.cmdb_ci_u_application_id || ''} · ${inc.cmdb_ci_display_value || ''}`}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inc.cmdb_ci_u_application_id || inc.cmdb_ci_display_value || '—'}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {inc.precursor_count > 0 ? (
                      <Chip label={inc.precursor_count} size="small"
                        sx={{ fontWeight: 700, bgcolor: '#ffebee', color: '#b71c1c', minWidth: 32 }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: '#aaa' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {inc.alert_signal_count > 0 ? (
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: '0.78rem', lineHeight: 1.8 }}>
                            <div><strong>Total alerts fired: {inc.alert_signal_count}</strong></div>
                            <div>⚡ Pre-incident (12h before): {inc.pre_incident_alert_count}</div>
                            <div>🔴 During incident: {inc.during_incident_alert_count}</div>
                            <div>Peak per period: {inc.peak_alert_count}</div>
                          </Box>
                        }
                        arrow
                      >
                        <Chip label={inc.alert_signal_count} size="small"
                          sx={{ fontWeight: 700, bgcolor: '#fff3e0', color: '#e65100', minWidth: 32, cursor: 'help' }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="No DB snapshot data — click to fetch live from Hedwig API" arrow>
                        <Chip
                          label="⚡ Live"
                          size="small"
                          clickable
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedNumber(inc.number);
                            setDrawerInitialTab(1);
                            setDrawerAutoFetch(true);
                          }}
                          sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: '#fff8e1', color: '#f57c00',
                            border: '1px solid #ffe082', cursor: 'pointer',
                            '&:hover': { bgcolor: '#fff3cd' } }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  {/* Pre-incident alerts (12h window) — standalone column */}
                  <TableCell align="center">
                    {inc.pre_incident_alert_count > 0 ? (
                      <Tooltip title={`${inc.pre_incident_alert_count} alerts fired in the 12h before this incident`} arrow>
                        <Chip label={inc.pre_incident_alert_count} size="small"
                          sx={{ fontWeight: 700, bgcolor: '#fff8e1', color: '#f57c00', minWidth: 32, cursor: 'help' }} />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#aaa' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell><ConfidenceChip value={inc.best_correlation_confidence} /></TableCell>
                  <TableCell><GapChip value={inc.gap_type} /></TableCell>
                </TableRow>
              ))}

              {!loading && sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#888' }}>
                    No incidents found for the selected filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalCount > PAGE_SIZE && (
          <Box sx={{ p: 1.5, borderTop: '1px solid #e0e6eb', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
            </Typography>
            <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)} variant="outlined">
              Previous
            </Button>
            <Button size="small" disabled={(page + 1) * PAGE_SIZE >= totalCount} onClick={() => setPage(p => p + 1)} variant="outlined">
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* Detail drawer */}
      <DetailDrawer
        detail={detail}
        loading={detailLoading}
        initialTab={drawerInitialTab}
        autoFetchLive={drawerAutoFetch}
        onClose={() => {
          setSelectedNumber(null);
          setDrawerInitialTab(0);
          setDrawerAutoFetch(false);
        }}
      />
    </Box>
  );
};

export default PreIncidentCorrelation;
